/**
 * Generate Document Dialog Component
 *
 * Modal dialog for configuring and generating a document from selected
 * patterns, gaps, and insights from knowledge synthesis.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState } from 'react';
import { Modal, Button, Label, TextInput, Select, Checkbox, Alert, Spinner } from 'flowbite-react';
import { HiDocumentText, HiX } from 'react-icons/hi';
import type { SelectedItems } from './PatternSelector';
import PatternSelector from './PatternSelector';
import type { Pattern, KnowledgeGap, StrategyInsight } from './KnowledgeHarvestButton';

interface GenerateDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  patterns: Pattern[];
  gaps: KnowledgeGap[];
  insights: StrategyInsight[];
  onGenerate: (document: GeneratedDocument) => void;
}

export interface GeneratedDocument {
  document_id: string;
  title: string;
  content: string;
  metadata: {
    generated_at: string;
    document_type: string;
    source_patterns: number;
    source_gaps: number;
    source_insights: number;
  };
}

const GenerateDocumentDialog: React.FC<GenerateDocumentDialogProps> = ({
  isOpen,
  onClose,
  patterns,
  gaps,
  insights,
  onGenerate
}) => {
  const [title, setTitle] = useState('Generated Standard');
  const [documentType, setDocumentType] = useState<'standard' | 'guideline' | 'reference'>('standard');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeExamples, setIncludeExamples] = useState(true);
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({
    patterns: [],
    gaps: [],
    insights: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate selection
      const totalSelected =
        selectedItems.patterns.length +
        selectedItems.gaps.length +
        selectedItems.insights.length;

      if (totalSelected === 0) {
        setError('Please select at least one pattern, gap, or insight to include in the document');
        setLoading(false);
        return;
      }

      // Import API utilities dynamically
      const { Make_Authorized_API_Call } = await import('../services/api/utils');
      const { API_ENDPOINTS } = await import('../services/api/endpoints');

      console.log('[GenerateDocument] Generating document...', {
        title,
        documentType,
        selectedItems: {
          patterns: selectedItems.patterns.length,
          gaps: selectedItems.gaps.length,
          insights: selectedItems.insights.length
        }
      });

      const result = await Make_Authorized_API_Call<GeneratedDocument>(
        API_ENDPOINTS.KNOWLEDGE.GENERATE,
        'POST',
        {
          title,
          documentType,
          includeMetadata,
          includeExamples,
          selectedPatterns: selectedItems.patterns,
          selectedGaps: selectedItems.gaps,
          selectedInsights: selectedItems.insights
        }
      );

      if (result.success && result.data) {
        console.log('[GenerateDocument] Document generated successfully:', {
          document_id: result.data.document_id,
          content_length: result.data.content.length
        });

        onGenerate(result.data);
        handleClose();
      } else {
        throw new Error(result.message || 'Failed to generate document');
      }
    } catch (err: any) {
      console.error('[GenerateDocument] Error:', err);
      setError(err.message || 'Failed to generate document');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form state
    setTitle('Generated Standard');
    setDocumentType('standard');
    setIncludeMetadata(true);
    setIncludeExamples(true);
    setSelectedItems({ patterns: [], gaps: [], insights: [] });
    setError(null);
    onClose();
  };

  const totalSelected =
    selectedItems.patterns.length +
    selectedItems.gaps.length +
    selectedItems.insights.length;

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      size="5xl"
      position="center"
    >
      <Modal.Header>
        <div className="flex items-center space-x-2">
          <HiDocumentText className="w-6 h-6 text-blue-600" />
          <span>Generate Document from Codebase Analysis</span>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-6">
          {/* Configuration section */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Document Title</Label>
              <TextInput
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter document title"
              />
            </div>

            <div>
              <Label htmlFor="documentType">Document Type</Label>
              <Select
                id="documentType"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value as any)}
              >
                <option value="standard">Standard</option>
                <option value="guideline">Guideline</option>
                <option value="reference">Reference</option>
              </Select>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <Label>Document Options</Label>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Checkbox
                  id="includeMetadata"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                />
                <Label htmlFor="includeMetadata" className="ml-2">
                  Include metadata (version, dates, changelog)
                </Label>
              </div>
              <div className="flex items-center">
                <Checkbox
                  id="includeExamples"
                  checked={includeExamples}
                  onChange={(e) => setIncludeExamples(e.target.checked)}
                />
                <Label htmlFor="includeExamples" className="ml-2">
                  Include code examples
                </Label>
              </div>
            </div>
          </div>

          {/* Error alert */}
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Pattern selector */}
          <div>
            <Label className="mb-2">
              Select Items to Include in Document
            </Label>
            <PatternSelector
              patterns={patterns}
              gaps={gaps}
              insights={insights}
              onSelectionChange={setSelectedItems}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-between w-full">
          <div className="text-sm text-gray-600">
            {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
          </div>
          <div className="flex space-x-2">
            <Button
              color="gray"
              onClick={handleClose}
              disabled={loading}
            >
              <HiX className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleGenerate}
              disabled={loading || totalSelected === 0}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <HiDocumentText className="w-4 h-4 mr-2" />
                  Generate Document
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateDocumentDialog;
